import { Autocomplete, BasicTable, Button, Typography } from '@fullscreendigital/carrefour-dex-fe'
import { useLocation, useNavigate } from 'react-router-dom'
import { useNotification } from '../../../../../../../hooks'
import React, { useState } from 'react'
import { useFormik } from 'formik'
import { formatErrorMessage } from '../../../../../../../utils/formatting'
import { useMutation, useQuery } from 'react-query'
import { makeCoreGetRequest, makeCorePostRequest } from '../../../../../../../utils/api'
import { URLS } from '../../../../constants'
import { CircularProgress, Grid } from '@mui/material'
import { DeleteActionIcon } from 'icons'
import s from '../../GoodsReception.modules.scss'

const CreateTruckStep2 = () => {
  const [selectedTicket, setSelectedTicket] = useState(null)
  const [navigateUrl, setNavigateUrl] = useState('')

  const navigate = useNavigate()
  const location = useLocation()
  const { showNotification } = useNotification()

  const { truckId } = location.state

  const { data: truckBls, isLoading: isLoadingTruckBls } = useQuery(
    ['truck-bls'],
    async () => {
      const response = await makeCoreGetRequest(
        `/shop-reception-mobile/truck/hub/truck-creation-with-bls-for-hub/${truckId}`
      )
      return response || []
    },
    {
      onError: error => {
        showNotification({
          message: `A aparut o eroare. ${formatErrorMessage(error)}`,
          type: 'error'
        })
      }
    }
  )

  const { data: hubName, isLoading: isLoadingHubName } = useQuery(
    ['hub-name'],
    async () => {
      const response = await makeCoreGetRequest('/shop-reception-mobile/home/hub-name')
      return response || []
    },
    {
      onError: error => {
        showNotification({
          message: `A aparut o eroare la obtinerea datelor despre hub. ${formatErrorMessage(
            error
          )}`,
          type: 'error'
        })
      }
    }
  )

  const saveBlForTruck = async (values: any) => {
    return await makeCorePostRequest(`/shop-reception-mobile/truck/shop/save-bls-for-truck`, values)
  }

  const { isLoading: isLoadingSaveBl, mutate } = useMutation(saveBlForTruck, {
    onSuccess: () => {
      showNotification({
        message: 'Camionul a fost salvat cu success.',
        type: 'success'
      })
      if (navigateUrl === URLS.AUTH_EXPECTED_TRUCKS_STEP2) {
        navigate(URLS.AUTH_EXPECTED_TRUCKS_STEP2, { state: { item: { value: truckId } } })
      } else {
        navigate(URLS.AUTH_EXPECTED_TRUCKS)
      }
    },
    onError: res => {
      showNotification({
        message: `A aparut o eroare ${formatErrorMessage(res)}`,
        type: 'error'
      })
    }
  })

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: {
      ...truckBls,
      deliveryTicketList: []
    },
    onSubmit: async (values: any) => {
      mutate(values)
    }
  })

  if (isLoadingTruckBls || isLoadingSaveBl || isLoadingHubName) {
    return <CircularProgress color="primary" style={{ margin: '40px auto' }} disableShrink />
  }

  return (
    <Grid container spacing={2} justifyContent={'center'}>
      <Typography
        component="h2"
        variant="medium"
        style={{ textAlign: 'center', textTransform: 'uppercase' }}
      >
        {hubName?.value}
      </Typography>

      <Grid xs={10} item display="flex" justifyContent="center">
        <Typography
          component="h4"
          variant="medium"
          style={{ textAlign: 'center', textTransform: 'uppercase', color: 'black' }}
        >
          {truckBls?.truckNumber} / {truckBls?.driverName}
        </Typography>
      </Grid>

      <Grid xs={10} item display="flex" justifyContent="center">
        <Typography
          component="h4"
          variant="medium"
          style={{ textAlign: 'center', textTransform: 'uppercase', color: 'black' }}
        >
          Sigilii: {truckBls?.concatenatedSeals}
        </Typography>
      </Grid>

      <Grid xs={10} item>
        <Autocomplete
          variant="outlined"
          label={`Adauga bonuri de livrare`}
          disableClearable
          options={truckBls?.deliveryTicketList.map(deliveryTicket => ({
            label: `${deliveryTicket.externalTicketId} - ${deliveryTicket.depositName}`,
            value: deliveryTicket.id
          }))}
          onChange={(event, option) => {
            const hasTicket = formik.values?.deliveryTicketList.some(
              ticket => ticket.id === option.value
            )

            if (!hasTicket) {
              formik.setFieldValue('deliveryTicketList', [
                ...formik.values.deliveryTicketList,
                truckBls?.deliveryTicketList.find(ticket => ticket.id === option.value)
              ])
            }
            setSelectedTicket(null)
          }}
          value={selectedTicket}
          blurOnSelect
        />
      </Grid>

      <Grid xs={10} item>
        <BasicTable
          columns={[
            {
              field: 'externalTicketId',
              title: 'BL'
            },
            {
              field: 'depositName',
              title: 'Depozit'
            },
            {
              field: '',
              title: '',
              render: row => (
                <div
                  onClick={() =>
                    formik.setFieldValue(
                      'deliveryTicketList',
                      formik.values?.deliveryTicketList &&
                        formik.values?.deliveryTicketList?.filter(ticket => ticket.id !== row.id)
                    )
                  }
                >
                  <DeleteActionIcon />
                </div>
              )
            }
          ]}
          rows={formik.values?.deliveryTicketList}
          containerProps={{
            className: s.basicTable
          }}
        />
      </Grid>

      <Grid xs={10} item>
        <Button
          variant="outlined"
          label="Incepe receptia"
          onClick={() => {
            setNavigateUrl(URLS.AUTH_EXPECTED_TRUCKS_STEP2)
            formik.handleSubmit()
          }}
          style={{ width: '100%' }}
          disabled={formik.values.deliveryTicketList.length === 0 || isLoadingSaveBl}
        />
      </Grid>
      <Grid xs={10} item>
        <Button
          variant="outlined"
          label="Salvare camion"
          onClick={() => {
            setNavigateUrl(URLS.AUTH_EXPECTED_TRUCKS)
            formik.handleSubmit()
          }}
          style={{ width: '100%' }}
          disabled={formik.values.deliveryTicketList.length === 0 || isLoadingSaveBl}
        />
      </Grid>
      <Grid xs={10} item>
        <Button
          type='button'
          variant='contained'
          label='Modifica Date Camion'
          onClick={() => navigate(URLS.AUTH_CREATE_TRUCK, {state: truckId })}
          style={{ width: '100%', marginTop: '15px' }}
        />
      </Grid>
    </Grid>
  )
}

export default CreateTruckStep2
